import { useEffect, useRef, useState } from "react";
import api, { streamSSE } from "@/lib/api";
import { Sparkles, Send, RefreshCw, Bot, AlertTriangle, Wand2 } from "lucide-react";

const AI_SID_KEY = "belo_ai_sid";

function getSid() {
  let v = localStorage.getItem(AI_SID_KEY);
  if (!v) {
    v = "ai-" + Math.random().toString(36).slice(2) + Date.now().toString(36);
    localStorage.setItem(AI_SID_KEY, v);
  }
  return v;
}

function inline(text, k) {
  const parts = text.split(/(\*\*[^*]+\*\*)/g).filter(Boolean);
  return parts.map((p, i) =>
    p.startsWith("**") && p.endsWith("**") ? (
      <strong key={`${k}-${i}`} className="font-semibold text-slate-900">{p.slice(2, -2)}</strong>
    ) : (
      <span key={`${k}-${i}`}>{p}</span>
    )
  );
}

function Markdown({ text }) {
  const lines = (text || "").split("\n");
  const out = [];
  let list = [];
  const flush = (k) => {
    if (list.length) {
      out.push(<ul key={`ul-${k}`} className="list-disc pl-5 space-y-1 my-2 text-slate-700">{list}</ul>);
      list = [];
    }
  };
  lines.forEach((raw, i) => {
    const line = raw.replace(/\s+$/, "").trimStart();
    if (/^(-{3,}|\*{3,}|_{3,})$/.test(line)) { flush(i); out.push(<hr key={i} className="my-3 border-slate-200" />); }
    else if (/^#{3}\s/.test(line)) { flush(i); out.push(<h4 key={i} className="font-heading text-sm font-bold text-slate-900 mt-4 mb-1">{inline(line.replace(/^#{3}\s/, ""), i)}</h4>); }
    else if (/^#{2}\s/.test(line)) { flush(i); out.push(<h3 key={i} className="font-heading text-base font-bold text-slate-900 mt-5 mb-1">{inline(line.replace(/^#{2}\s/, ""), i)}</h3>); }
    else if (/^#\s/.test(line)) { flush(i); out.push(<h2 key={i} className="font-heading text-lg font-bold text-slate-900 mt-5 mb-2">{inline(line.replace(/^#\s/, ""), i)}</h2>); }
    else if (/^[-*]\s/.test(line)) { list.push(<li key={i}>{inline(line.replace(/^[-*]\s/, ""), i)}</li>); }
    else if (/^\d+[.)]\s/.test(line)) { list.push(<li key={i}>{inline(line.replace(/^\d+[.)]\s/, ""), i)}</li>); }
    else if (line.trim() === "") { flush(i); }
    else { flush(i); out.push(<p key={i} className="text-slate-700 my-1.5 leading-relaxed">{inline(line, i)}</p>); }
  });
  flush("end");
  return <div className="text-sm">{out}</div>;
}

function friendlyError(e) {
  if (/budget|exceeded|credit/i.test(e || ""))
    return "Your Emergent Universal Key is out of credits. Add balance in Profile → Universal Key → Add Balance, then try again.";
  return e || "Something went wrong. Please try again.";
}

const SUGGESTIONS = [
  "What 5 keywords should I target this month?",
  "How do I beat Turo for Logan Airport searches?",
  "Where are my best leads coming from?",
  "Write a Google Business Profile post for airport travelers.",
];

export default function MarketingAI({ days = 30 }) {
  const [report, setReport] = useState("");
  const [genLoading, setGenLoading] = useState(false);
  const [genErr, setGenErr] = useState("");

  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [chatLoading, setChatLoading] = useState(false);
  const [chatErr, setChatErr] = useState("");
  const sessionId = useRef(getSid());
  const threadRef = useRef(null);

  useEffect(() => {
    let active = true;
    api.get(`/admin/ai/chat/${sessionId.current}`).then((r) => {
      if (active) setMessages(r.data?.messages || []);
    }).catch(() => {});
    return () => { active = false; };
  }, []);

  useEffect(() => {
    if (threadRef.current) threadRef.current.scrollTop = threadRef.current.scrollHeight;
  }, [messages]);

  const generateReport = () => {
    if (genLoading) return;
    setGenLoading(true); setGenErr(""); setReport("");
    streamSSE(`/admin/ai/insights?days=${days}`, {
      onDelta: (d) => setReport((p) => p + d),
      onError: (e) => setGenErr(friendlyError(e)),
    }).finally(() => setGenLoading(false));
  };

  const sendChat = (text) => {
    const msg = (text ?? input).trim();
    if (!msg || chatLoading) return;
    setInput(""); setChatErr("");
    setMessages((prev) => [...prev, { role: "user", content: msg }, { role: "assistant", content: "" }]);
    setChatLoading(true);
    streamSSE(`/admin/ai/chat`, {
      body: { session_id: sessionId.current, message: msg, days },
      onDelta: (d) =>
        setMessages((prev) => {
          const c = [...prev];
          const last = c.length - 1;
          c[last] = { ...c[last], content: (c[last].content || "") + d };
          return c;
        }),
      onError: (e) => setChatErr(friendlyError(e)),
    }).finally(() => setChatLoading(false));
  };

  return (
    <div data-testid="marketing-ai" className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
      <div className="flex items-center gap-3 px-6 py-5 bg-gradient-to-r from-slate-900 to-blue-900 text-white">
        <span className="grid place-items-center w-10 h-10 rounded-xl bg-white/15"><Sparkles size={20} /></span>
        <div>
          <h3 className="font-heading text-lg font-bold leading-tight">Belo Growth AI</h3>
          <p className="text-xs text-blue-100">SEO &amp; marketing strategist trained on your live analytics</p>
        </div>
      </div>

      <div className="p-6 space-y-6">
        {/* Weekly insights report */}
        <div>
          <div className="flex items-center justify-between gap-3 mb-3">
            <h4 className="font-heading font-bold text-slate-900">Weekly Insights &amp; Recommendations</h4>
            <button
              data-testid="ai-generate-report-btn"
              onClick={generateReport}
              disabled={genLoading}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold disabled:opacity-60 transition-colors"
            >
              {genLoading ? <RefreshCw size={16} className="animate-spin" /> : <Wand2 size={16} />}
              {genLoading ? "Analyzing…" : report ? "Regenerate" : "Generate report"}
            </button>
          </div>
          {genErr && (
            <div className="flex items-start gap-2 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-xl p-3 mb-3">
              <AlertTriangle size={16} className="mt-0.5 shrink-0" /><span>{genErr}</span>
            </div>
          )}
          <div data-testid="ai-report" className="rounded-xl border border-slate-200 bg-slate-50/60 p-4 min-h-[120px]">
            {report ? <Markdown text={report} /> : (
              <p className="text-sm text-slate-500">
                Click <span className="font-medium text-slate-700">Generate report</span> for a prioritized, data-driven
                growth plan — what's working, where your leads come from, and exactly how to win Logan Airport / Boston
                searches against Turo.
              </p>
            )}
          </div>
        </div>

        {/* Chat */}
        <div>
          <h4 className="font-heading font-bold text-slate-900 mb-3">Ask the strategist</h4>
          <div ref={threadRef} data-testid="ai-chat-thread" className="rounded-xl border border-slate-200 bg-white max-h-80 overflow-y-auto p-4 space-y-4">
            {messages.length === 0 && (
              <div className="flex flex-wrap gap-2">
                {SUGGESTIONS.map((s) => (
                  <button key={s} onClick={() => sendChat(s)} className="text-xs px-3 py-1.5 rounded-full border border-slate-200 text-slate-600 hover:border-blue-400 hover:text-blue-700 transition-colors">
                    {s}
                  </button>
                ))}
              </div>
            )}
            {messages.map((m, i) => (
              <div key={i} className={`flex gap-3 ${m.role === "user" ? "justify-end" : ""}`}>
                {m.role === "assistant" && <span className="grid place-items-center w-8 h-8 rounded-lg bg-blue-100 text-blue-700 shrink-0"><Bot size={16} /></span>}
                <div className={`rounded-2xl px-4 py-2.5 max-w-[80%] ${m.role === "user" ? "bg-blue-600 text-white" : "bg-slate-100 text-slate-800"}`}>
                  {m.role === "assistant"
                    ? (m.content ? <Markdown text={m.content} /> : <span className="text-slate-400 text-sm">Thinking…</span>)
                    : <span className="text-sm whitespace-pre-wrap">{m.content}</span>}
                </div>
              </div>
            ))}
          </div>
          {chatErr && (
            <div className="flex items-start gap-2 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-xl p-3 mt-3">
              <AlertTriangle size={16} className="mt-0.5 shrink-0" /><span>{chatErr}</span>
            </div>
          )}
          <div className="flex items-center gap-2 mt-3">
            <input
              data-testid="ai-chat-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") sendChat(); }}
              placeholder="Ask about SEO, keywords, leads, or how to beat Turo…"
              className="flex-1 px-4 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <button
              data-testid="ai-chat-send-btn"
              onClick={() => sendChat()}
              disabled={chatLoading || !input.trim()}
              className="inline-flex items-center justify-center w-11 h-11 rounded-xl bg-slate-900 hover:bg-slate-800 text-white disabled:opacity-50 transition-colors"
            >
              {chatLoading ? <RefreshCw size={18} className="animate-spin" /> : <Send size={18} />}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
